/* ============================================================================
 * rtl-numerals.js — keep numeric VALUES readable in the Arabic layout.
 *
 * The defect this exists for, measured 2026-08-17 across all 678 public tools:
 * a short text run made only of digits and separators has no strong-direction
 * character to anchor it, so in an RTL paragraph the bidi algorithm reorders
 * it. "1280 × 900" is painted "900 × 1280"; "0:15 / 3:42" becomes "3:42 / 0:15";
 * CMYK "0%, 50%, 86%, 45%" reads back-to-front. The clipboard and the DOM are
 * right — only the eye is misled, which is why no text-based test ever caught
 * it. 51 tools were affected: a DOM scan found the candidates, and a
 * measurement of Range x-positions proved which ones actually reverse.
 *
 * Why a runtime pass and not 51 CSS edits: the values are drawn under 34
 * different class names (12 sites carry no class at all), most are rewritten
 * several times a second by a player clock, and the next tool would
 * reintroduce the bug. This marks the element once; the rule lives in app.css.
 *
 * Loaded ONLY on RTL locales — the other eight pay nothing.
 * Guard: tests/playwright/rtl-numerals.mjs
 * ========================================================================== */
(function () {
    'use strict';
    if (document.documentElement.getAttribute('dir') !== 'rtl') return;

    /* Digits and the separators that appear BETWEEN them. No letters of any
     * script: prose carrying a number is anchored by its own letters and must
     * not be touched. */
    var NUMERIC = /^[0-9\s.,;:%×xX/°+–—\-()[\]]+$/;
    var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, CODE: 1, PRE: 1 };
    /* WeakSet, not dataset: `el.dataset.x = '1'` writes a real attribute and
     * allocates a DOMStringMap on every read. This flag is consulted for every
     * text node on the page, so it has to cost nothing. */
    var seen = new WeakSet();

    function eligible(el, text) {
        if (!el || el.nodeType !== 1) return false;
        if (SKIP_TAGS[el.tagName]) return false;
        if (seen.has(el)) return false;
        // Never fight a tool that already made its own decision.
        var inline = el.style && el.style.direction;
        if (inline === 'ltr' || inline === 'rtl') return false;
        /* The whole element must BE the value. A number inside a sentence
         * shares its parent with words, and forcing that parent to LTR would
         * scramble the sentence — the same bug pointing the other way.
         *
         * The child count is checked BEFORE textContent on purpose: textContent
         * concatenates the entire subtree, so asking a page-level container for
         * it costs the whole document. Measured at 4× CPU throttle, that single
         * call was most of the 265 ms this pass used to add. Anything with more
         * than a couple of children is a container, not a value. */
        var kids = el.childNodes.length;
        if (kids > 3) return false;
        if (kids !== 1 && !NUMERIC.test((el.textContent || '').trim())) return false;
        return NUMERIC.test(text) && /\d/.test(text);
    }

    function fixNode(node) {
        var text = (node.nodeValue || '').trim();
        if (text.length < 2 || text.length > 48) return;
        // Cheapest rejections first: most text on a page fails these.
        if (!NUMERIC.test(text)) return;
        // A single number needs no help: bidi keeps its own digits in order.
        if ((text.match(/\d+/g) || []).length < 2) return;
        var el = node.parentElement;
        if (!eligible(el, text)) return;
        seen.add(el);
        el.classList.add('rtl-num');
    }

    function scan(root) {
        if (!root) return;
        if (root.nodeType === 3) { fixNode(root); return; }
        if (root.nodeType !== 1) return;
        var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        /* Budget is a backstop against a pathological document, not a coverage
         * limit: the biggest real page measured 1763 text nodes in <main> and a
         * full walk costs 0.2 ms, so this ceiling is ~10× the worst real case. */
        var n, budget = 20000;
        while ((n = walker.nextNode()) && budget-- > 0) fixNode(n);
    }

    function start() {
        /* First pass covers the content only. The sidebar carries a link per
         * tool and the header/footer carry none of the values this fixes, so
         * walking the whole body doubled the node count for nothing. Anything
         * that appears outside <main> later — a toast, a modal — still arrives
         * through the observer below. */
        scan(document.querySelector('main') || document.body);

        /* Values are re-rendered constantly (a player clock ticks several times
         * a second), so a one-shot pass would only cover what existed at load.
         * Work is coalesced into one idle callback and each element is marked
         * once, so a ticking clock costs a dataset lookup, not a re-scan. */
        var pending = [], scheduled = false;
        var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 60); };

        /* Chunked, but nothing is dropped. The first version processed the
         * first 200 records and threw the rest away, which silently lost
         * coverage on any page that builds a long list in many mutations —
         * /visa-free-checker rendered 19 dates and none were marked. The tail
         * goes back into the queue and gets its own callback instead. */
        function flush() {
            scheduled = false;
            var batch = pending.splice(0, 200);
            for (var i = 0; i < batch.length; i++) scan(batch[i]);
            if (pending.length && !scheduled) { scheduled = true; idle(flush); }
        }

        new MutationObserver(function (muts) {
            for (var i = 0; i < muts.length; i++) {
                var m = muts[i];
                if (m.type === 'characterData') pending.push(m.target);
                else for (var j = 0; j < m.addedNodes.length; j++) pending.push(m.addedNodes[j]);
            }
            if (pending.length && !scheduled) { scheduled = true; idle(flush); }
        }).observe(document.body, { childList: true, subtree: true, characterData: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
})();
